import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
  Grid,
  Table,
  Progress,
  Segment,
  Loader,
  Dimmer,
} from 'semantic-ui-react';
import CountUp from 'react-countup';
import qs from 'qs';

import { decodeSearch } from '../../utils/url';
import config from '../../../config';
import { fetchM } from '../../actions/fetchM';
import { flashErrorMessage } from '../../actions/flash';
import { TRANSLATION_FILES } from '../../constants/translation';

class DelistReasonsByStatus extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    barChart: PropTypes.bool,
    headerColor: PropTypes.string,
    location: PropTypes.shape({ 
      pathname: PropTypes.string,
      query: PropTypes.object,
      search: PropTypes.string,
    }).isRequired,
    projectID: PropTypes.string,
    t: PropTypes.func,
  }
  static mapStateToProps = () => ({});
  static getQueryData = (props) => {
    const query = decodeSearch(props.location.search);
    const projectID = query.projectID || '';
    const projectUnitLayoutID = query.projectUnitLayoutID || '';
    const dateRange = query.dateRange || '';
    const dateFrom = query.dateFrom ? query.dateFrom : '';
    const dateTo = query.dateTo ? query.dateTo : '';
    const businessUnitID = query.businessUnitID ? query.businessUnitID : '';
    return {
      projectID,
      projectUnitLayoutID,
      dateRange,
      dateFrom,
      dateTo,
      businessUnitID,
    };
  }
  constructor(props) {
    super(props);
    this.state = {
      reportData: [],
      reasonsMap: {},
      loading: true,
      refetch: true,
    };
  }
  componentDidMount() {
    this.fetchDelistReasonDefinitions();
    this.fetchReports();
  }
  componentWillReceiveProps(nextProps) {
    const query = DelistReasonsByStatus.getQueryData(this.props);
    const newQuery = DelistReasonsByStatus.getQueryData(nextProps);
    const refetch = newQuery.dateRange === 'custom' ? !!(newQuery.dateFrom && newQuery.dateTo) : true;
    this.setState({
      refetch,
    });
    if (
      (query.dateRange !== newQuery.dateRange) ||
      (query.dateTo !== newQuery.dateTo) ||
      (query.dateFrom !== newQuery.dateFrom) ||
      (query.projectID !== newQuery.projectID) ||
      (query.businessUnitID !== newQuery.businessUnitID) ||
      (newQuery.projectID !== '' && query.projectUnitLayoutID !== newQuery.projectUnitLayoutID)
    ) {
      if (refetch) {
        this.setState({
          reportData: [],
        }, () => {
          this.fetchReports(nextProps);
        });
      }
    }
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  // The report endpoint returns the reason label, but we also fallback to the master
  // delist-reason list (fetched from /settings/delist_reasons/) for additional lookup
  getReasonLabel = (reasonReport) => {
    const { t } = this.props;
    const { reasonsMap } = this.state;
    if (!reasonReport) {
      return t('No Reason');
    }
    // Candidate identifiers that could appear in the report payload.
    const candidates = [
      reasonReport.delistReasonID,
      reasonReport.delistReasonId,
      reasonReport.delistReasonName,
      reasonReport.delistReason,
      reasonReport.reasonID,
      reasonReport.name,
      reasonReport.id,
    ].filter(v => v != null && v !== '');
    // Look the candidate up in the master map (keyed by id and by name).
    for (let i = 0; i < candidates.length; i += 1) {
      const key = candidates[i];
      if (reasonsMap[key]) {
        return reasonsMap[key];
      }
    }
    // Fall back to any label the report itself provided, then the raw name.
    const fallback =
      reasonReport.delistReasonLabel ||
      reasonReport.reasonLabel ||
      reasonReport.label ||
      reasonReport.delistReasonName ||
      reasonReport.name;
    return fallback || t('No Reason');
  }
  getBarPercent = (data, count) => {
    const total = Math.max(...data
      .filter(m => m.delistReasonID !== 'total')
      .map(m => m.count));
    if (total) {
      return Math.floor((count / total) * 100);
    }
    return 0;
  }
  getBarColor = (reasonReport) => {
    const { t } = this.props;
    const reasonLabel = this.getReasonLabel(reasonReport);

    if (reasonLabel === t('No Reason') || !reasonReport.delistReasonID || reasonReport.delistReasonID === '') {
      return 'grey';
    }

    const colors = ['blue', 'orange', 'green', 'teal', 'purple', 'pink', 'yellow', 'red'];
    const hash = (reasonReport.delistReasonID || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }
  checkData = (reportData) => {
    if (!reportData || reportData.length === 0) {
      return [];
    }
    // Calculate total
    const total = reportData.reduce((sum, item) => sum + item.count, 0);
    return [
      ...reportData,
      {
        delistReasonID: 'total',
        delistReasonLabel: 'Total',
        count: total,
      },
    ];
  }
  async fetchDelistReasonDefinitions() {
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM('/settings/delist_reasons/', {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      return;
    }
    if (res && res.ok) {
      const reasons = await res.json();
      const reasonsMap = {};
      (reasons || []).forEach((reason) => {
        if (reason.id) {
          reasonsMap[reason.id] = reason.label;
        }
        if (reason.name) {
          reasonsMap[reason.name] = reason.label;
        }
      });
      await this.setComponentState({ reasonsMap });
    }
  }
  async fetchReports(props = this.props) {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    const q = decodeSearch(props.location.search);
    const query = Object.assign({}, q);
    delete query.projectID;
    delete query.projectUnitLayoutID;
    const projectID = (props.projectID || q.projectID) || '';
    const projectUnitLayoutID = q.projectUnitLayoutID || '';
    if (projectID !== '') {
      query.projectID = projectID;
      if (projectUnitLayoutID !== '') {
        query.projectUnitLayoutID = projectUnitLayoutID;
      }
    }
    let res;
    try {
      const apiUrl = `${config.get('bookApiURL')}/reports/project_units/delist_reasons/`;
      res = await store.dispatch(fetchM(`${apiUrl}?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const reportData = await res.json();
      await this.setComponentState({
        reportData,
      });
    }
    await this.setLoading(false);
  }
  renderTable = () => {
    const { barChart, headerColor, t } = this.props;
    const data = this.checkData(this.state.reportData);
    if (data && data.length > 0) {
      return (
        <Table
          basic={barChart && 'very'}
          celled={!barChart}
          structured={!barChart}
          color={!barChart ? headerColor : null}
          compact
          unstackable
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell collapsing>{t('Delist Reason')}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right" collapsing style={{ paddingLeft: '17em' }}>
                {!barChart && t('Count')}
              </Table.HeaderCell>
              {barChart && <Table.HeaderCell textAlign="left">{t('Count')}</Table.HeaderCell>}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              data.map((reasonReport, idx) => {
                const isTotal = reasonReport.delistReasonID === 'total';
                const boldStyle = isTotal ? { fontWeight: 'bold' } : {};
                return (
                  <Table.Row key={idx}>
                    <Table.Cell collapsing style={boldStyle}>
                      {isTotal ? t('Total') : this.getReasonLabel(reasonReport)}
                    </Table.Cell>
                    <Table.Cell
                      textAlign="right"
                      collapsing
                      style={{ ...boldStyle, paddingLeft: '3em' }}
                    >
                      {
                        barChart ? (
                          <CountUp
                            duration={2.1}
                            end={this.state.loading ? 0 : reasonReport.count}
                            start={0}
                            useEasing
                          />
                        ) : reasonReport.count
                      }
                    </Table.Cell>
                    {
                      barChart &&
                      <Table.Cell textAlign="left" className="hide-mobile-six-hundred-px">
                        {
                          !isTotal &&
                          <div style={{ width: '320px', maxWidth: '100%' }}>
                            <Progress
                              className="progress-barchart"
                              color={this.getBarColor(reasonReport)}
                              percent={this.state.loading
                                ? 0
                                : this.getBarPercent(data, reasonReport.count)
                              }
                            />
                          </div>
                        }
                      </Table.Cell>
                    }
                  </Table.Row>
                );
              })
            }
          </Table.Body>
        </Table>
      );
    }
    return (
      <div style={{ padding: '1em', textAlign: 'center', color: '#999' }}>
        {t('No delist reasons recorded yet')}
      </div>
    );
  }
  render() {
    const { barChart, headerColor, t } = this.props;
    return (
      <Grid style={{ margin: '-1rem 0' }}>
        <Grid.Row style={barChart && { paddingTop: '0' }}>
          <Grid.Column>
            <Dimmer
              inverted style={{ zIndex: 20 }}
              active={this.state.loading}
            >
              <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
            </Dimmer>
            {
              barChart ? (
                <Segment color={headerColor}>
                  {this.renderTable()}
                </Segment>
              ) : this.renderTable()
            }
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default connect(
  DelistReasonsByStatus.mapStateToProps,
)(withTranslation()(DelistReasonsByStatus));



// WEBPACK FOOTER //
// ./src/components/Reports/DelistReasonsByStatus.jsx